import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchBenefitPlans, fetchBenefitPlansByAccountKey } from "../../utils/apiUtils";
import { useEffect, useState, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import SharedTable from "./SharedTable";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import { AsyncPaginate } from "react-select-async-paginate";
import { useLocation, useNavigate } from "react-router";
import { useMultiStepFormStore } from "../../store/multiStepFormStore";
import debounce from "lodash.debounce";
import { convertPlanDetailToFormData, generatePayloadFromPlanDetials, getPlanDetailsByBenefitPlanHccId } from "../../utils/benefitPlanUtils";

const limit = 20;
const BenefitPlans = () => {
    const location = useLocation();
    const [searchValue, setSearchValue] = useState(location.state?.benefitPlanName);
    const [decouncedSearchValue, setDecouncedSearchValue] = useState(location.state?.benefitPlanName);
    const [account, setAccount] = useState(location.state?.acount);
    const [showMoreColumns, setShowMoreColumns] = useState(false);

    const navigate = useNavigate();
    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status, refetch } = useInfiniteQuery({
        queryKey: ["benefitPlans", limit, account, decouncedSearchValue],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};

            if (decouncedSearchValue) {
                params.condition = 'OR'
                params.benefitPlanHccId = decouncedSearchValue;
                params.benefitPlanName = decouncedSearchValue;
            }

            if (account) {
                params.accountKey = account.additional.accountKey;
                const plansByAccountKey = await fetchBenefitPlansByAccountKey(params);
                return { rows: plansByAccountKey, count: plansByAccountKey.length, offset: 0 };
            }

            return await fetchBenefitPlans({ ...params, limit });
        },

        select: (data) => {
            //   console.log({ data });
            return {
                count: data.pages?.[0]?.count,
                rows: data.pages.map((page) => page.rows).flatMap((x) => x)
                //    .sort((a, b) => b.versionEffDateKey - a.versionEffDateKey)
            };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;

            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: true,
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const { upsertDraft } = useMultiStepFormStore();

    let columns = ["benefitPlanHccId", "benefitPlanName", "benefitPlanDesc", 'planEffectiveDateKey', "versionEffDateKey",
        "versionExpDateKey",];

    if (showMoreColumns) {
        columns = [
            ...columns,

            "benefitPlanKey",
            "benefitPlanHistoryFactKey",
            "benefitPlanStatus",
            "planEffectiveDateKey",
            "planTerminationDateKey",
            "coverageTierSetKey",
            'planYrStartDtOverrideKey',
            'planYrEndDtOverrideKey'

        ];
    }

    const onDetailHandler = async (body) => {
        return await getPlanDetailsByBenefitPlanHccId(body)
    }

    const onEditHandler = async ({ data }) => {
        const draft = await convertPlanDetailToFormData({ payload: data })
        upsertDraft(draft)
        navigate(`/config/plan-builder/${encodeURIComponent(draft.draftId)}`);
        return
    }

    return (
        <div className={"container "}>
            <p className="title">Benefit Plans  ( {data?.count || 0} )</p>
            <p className="sub-title">Benefit Plans</p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    <AsyncPaginate
                        placeholder={"Select an account"}
                        className="react-select-container large-input"
                        classNamePrefix={"react-select"}
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={setAccount}
                        styles={customStyles}
                        isClearable
                    />

                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        // list="items"
                        autoComplete="off"
                        // onFocus={() => setCurrentPage(0)}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setAccount(null);
                        setDecouncedSearchValue("");
                        setSearchValue("");
                    }}
                >
                    Clear filters
                </p>

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"benefitplan"}
                        columns={columns}
                        items={data?.rows}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onEditHandler={onEditHandler}
                        onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}
        </div>
    );
};

export default BenefitPlans;
